import React from 'react';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { createURLFromLocation, modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import type { LoginFormData } from '@jwp/ott-common/types/account';
import { useExternalLoginProviders } from '@jwp/ott-hooks-react/src/useExternalLoginProviders';

import LoginForm from '../../../components/LoginForm/LoginForm';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';
import useQueryParam from '../../../hooks/useQueryParam';

const Login = () => {
  const accountController = getModule(AccountController);

  const { siteName } = useConfigStore((s) => s.config);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();
  const emailParam = useQueryParam('email') || '';
  const messageParam = useQueryParam('message') || '';
  const returnUrl = useQueryParam('returnUrl') || '';

  const { data: externalProviders } = useExternalLoginProviders(window.location.href.split('?')[0]);

  const { values, errors, submitting, validationSchemaError, handleSubmit, handleChange } = useForm<LoginFormData>({
    initialValues: { email: emailParam, password: '' },
    validationSchema: object().shape({
      email: string()
        .email(t('login.field_is_not_valid_email'))
        .required(t('login.field_required', { field: t('login.email') })),
      password: string().required(t('login.field_required', { field: t('login.password') })),
    }),
    onSubmit: async ({ email, password }) => {
      const challenge = await accountController.login(email, password, window.location.href);

      if (challenge) {
        navigate(modalURLFromLocation(location, 'challenge', { id: challenge.id, returnUrl }));
      } else {
        announce(t('login.sign_in_success'), 'success');
        navigate(returnUrl || modalURLFromLocation(location, null), { replace: true });
      }
    },
    onSubmitError: ({ resetValue }) => {
      if (messageParam) navigate(createURLFromLocation(location, { message: undefined }));
      resetValue('password');
    },
  });

  return (
    <LoginForm
      values={values}
      validationError={validationSchemaError}
      errors={errors}
      submitting={submitting}
      siteName={siteName}
      onSubmit={handleSubmit}
      onChange={handleChange}
      hasExternalLogin={!!externalProviders?.length}
      message={messageParam}
    />
  );
};

export default Login;
